import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FaBook,
  FaClipboardList,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import academicStyles from "../../../../pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import styles from "./Remedialcreate.module.css";
import viewStyles from "./ViewRemedial.module.css"; // ⬅ reuse view styles
import AxiosInstance, { TokenService } from "../../../../services/Api";
import {
  CustomButton,
  CustomTextInput,
  CustomDropdown,
} from "../../../../components";
import PopupModal from "../../../../components/PopupModal/PopupModal";

// ==================== CONSTANTS ====================
const DIFFICULTY_OPTIONS = [
  { value: "Easy", label: "Easy" },
  { value: "Medium", label: "Medium" },
  { value: "Hard", label: "Hard" },
];

const MCQ_OPTION_OPTIONS = [
  { value: 2, label: "2" },
  { value: 3, label: "3" },
  { value: 4, label: "4" },
  { value: 5, label: "5" },
];

const MAX_QUESTIONS = 10;
const MAX_REGENERATE = 3;
const OPTION_LETTERS = ["a", "b", "c", "d", "e", "f"];

// ------------------------------------------------------------------
// Normalize a question coming from EITHER endpoint into one shape.
// ------------------------------------------------------------------
const normalizeQuestion = (q = {}) => ({
  question_no: q.question_no,
  question: q.question || q.assessment_question || "",
  correct_option: (q.correct_option || q.correct_answer || "")
    .toString()
    .toLowerCase(),
  student_option: (q.student_option || q.student_answer || "")
    .toString()
    .toLowerCase(),
  explanation: q.explanation || "",
  option_a: q.option_a,
  option_b: q.option_b,
  option_c: q.option_c,
  option_d: q.option_d,
  option_e: q.option_e,
  option_f: q.option_f,
});

const getOptionCountFromQuestion = (q = {}) => {
  let count = 0;
  for (const letter of OPTION_LETTERS) {
    const v = q[`option_${letter}`];
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      count += 1;
    }
  }
  return count || 4;
};

function RemedialCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, translations } = useSelector((state) => state.language || {});

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const client_id = clientData.client_id;

  const school_id = clientData?.school_id || "";
  const user_id = user?.id || "";

  const routeState = location.state || {};
  const assessment_id =
    routeState.assessment_id ?? routeState.assessmentId ?? "";
  const student_id = routeState.student_id ?? routeState.studentId ?? "";

  const [formData, setFormData] = useState({
    num_questions: "",
    difficulty_level: "",
    mcq_options: "",
    timeDuration: "",
  });

  const [errors, setErrors] = useState({});

  const [wrongQuestions, setWrongQuestions] = useState([]);
  const [studentName, setStudentName] = useState("");
  const [assessmentMeta, setAssessmentMeta] = useState({});
  const [loadingAssessment, setLoadingAssessment] = useState(false);

  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [regenerateCount, setRegenerateCount] = useState(0);
  const [hasGeneratedOnce, setHasGeneratedOnce] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [alreadyExists, setAlreadyExists] = useState(false);
  const [existingRemedialId, setExistingRemedialId] = useState(null);

  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
  });

  const applyRemedialPayload = (respData, { lock = false } = {}) => {
    const data = respData?.data || {};
    const assessment = data.assessment || data.remedial || {};

    setAssessmentMeta({
      assessment_id: data.assessment_id ?? assessment.id ?? assessment_id,
      book_id: assessment.book_id ?? "",
      book_name: assessment.book_name ?? "",
      class: assessment.class ?? "",
      class_name: assessment.class_name ?? "",
      section_id: assessment.section_id ?? "",
      medium: assessment.medium ?? "",
      subject: assessment.subject ?? "",
      topic: assessment.topic ?? "",
      sub_topic: assessment.sub_topic ?? "",
      start_page: assessment.start_page ?? "",
      end_page: assessment.end_page ?? "",
      pass_percentage: assessment.pass_percentage ?? "",
      uploaded_file_path: assessment.uploaded_file_path ?? "",
      mcq_options: assessment.mcq_options_count ?? "",
    });

    setStudentName(data.student_name || "");

    setFormData({
      num_questions:
        assessment.num_questions != null
          ? String(assessment.num_questions)
          : "",
      difficulty_level: assessment.difficulty_level || "",
      timeDuration: assessment.duration != null
        ? String(assessment.duration)
        : "",
      mcq_options:
        assessment.mcq_options_count != null
          ? assessment.mcq_options_count
          : assessment.mcq_options != null
            ? assessment.mcq_options
            : "",
    });
    const wrongs = Array.isArray(data.wrong_questions)
      ? data.wrong_questions.map(normalizeQuestion)
      : [];
    setWrongQuestions(wrongs);

    const generated =
      data.remedial_questions ||
      data.generated_questions ||
      data.questions ||
      [];
    setGeneratedQuestions(
      Array.isArray(generated) ? generated.map(normalizeQuestion) : [],
    );

    if (lock) {
      setAlreadyExists(true);
    } else {
      setAlreadyExists(false);
      setExistingRemedialId(null);
    }
  };

  const fetchExistingRemedial = async (remedialId) => {
    try {
      const payload = {
        school_id,
        assessment_id,
        student_id,
        id: remedialId,
      };

      const response = await AxiosInstance.post(
        `/book/remedialView/view`,
        payload,
      );

      const respData = response?.data || {};
      if (respData.status || respData.success) {
        setExistingRemedialId(remedialId);
        applyRemedialPayload(respData, { lock: true });
      } else {
        setModalState({
          open: true,
          type: "Failed",
          message:
            respData.message ||
            t?.failedToLoadAssessment ||
            "Failed to load existing remedial assessment",
        });
      }
    } catch (error) {
      console.error("Error loading existing remedial:", error);
      setModalState({
        open: true,
        type: "Failed",
        message:
          error?.response?.data?.message ||
          t?.failedToLoadAssessment ||
          "Failed to load existing remedial assessment",
      });
    }
  };

  const fetchRemedialAssessment = useCallback(async () => {
    if (!school_id || !assessment_id || !student_id) return;

    setLoadingAssessment(true);
    try {
      const payload = { school_id, assessment_id, student_id };

      const response = await AxiosInstance.post(
        `/book/createRemedialAssessment`,
        payload,
      );

      const respData = response?.data || {};

      if (respData.status || respData.success) {
        applyRemedialPayload(respData);
      } else if (respData.remedial_id) {
        await fetchExistingRemedial(respData.remedial_id);
      } else {
        setAlreadyExists(false);
        setExistingRemedialId(null);
        setWrongQuestions([]);
        setModalState({
          open: true,
          type: "Failed",
          message:
            respData.message ||
            t?.failedToLoadAssessment ||
            "Failed to load remedial assessment",
        });
      }
    } catch (error) {
      console.error("Error loading remedial assessment:", error);
      setWrongQuestions([]);
      setModalState({
        open: true,
        type: "Failed",
        message:
          error?.response?.data?.message ||
          t?.failedToLoadAssessment ||
          "Failed to load remedial assessment",
      });
    } finally {
      setLoadingAssessment(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_id, assessment_id, student_id, t]);

  useEffect(() => {
    fetchRemedialAssessment();
  }, [fetchRemedialAssessment]);

  const handleDropdownChange = (name, selectedOption) => {
    if (alreadyExists) return;
    const value = selectedOption?.value ?? "";
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleInputChange = (e) => {
    if (alreadyExists) return;
    const { name, value } = e.target;

    if (name === "num_questions") {
      if (value === "") {
        setFormData((prev) => ({ ...prev, num_questions: "" }));
        setErrors((prev) => ({ ...prev, num_questions: "" }));
        return;
      }
      if (!/^\d+$/.test(value)) return;
      if (value.startsWith("0")) return;
      if (value.length > 2) return;
      if (Number(value) > MAX_QUESTIONS) return;

      setFormData((prev) => ({ ...prev, num_questions: value }));
      setErrors((prev) => ({ ...prev, num_questions: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateGenerate = () => {
    const newErrors = {};

    if (!formData.num_questions) {
      newErrors.num_questions =
        t?.numQuestionsRequired || "Number of Questions is required";
    } else if (!formData.timeDuration) {
      newErrors.timeDuration = t?.timeDurationRequired || "Time Duraion is required";
    } else if (/^0/.test(formData.num_questions)) {
      newErrors.num_questions = t?.numQuestionsNoLeadingZero || "Number cannot start with 0";
    } else if (Number(formData.num_questions) > MAX_QUESTIONS) {
      newErrors.num_questions =
        t?.maxQuestionsAllowed || `Maximum ${MAX_QUESTIONS} questions allowed`;
    } else if (Number(formData.num_questions) < 1) {
      newErrors.num_questions =
        t?.minQuestionRequired || "Minimum 1 question required";
    }

    if (!formData.difficulty_level) {
      newErrors.difficulty_level =
        t?.difficultyLevelRequired || "Difficulty Level is required";
    }

    if (!formData.mcq_options) {
      newErrors.mcq_options =
        t?.mcqOptionsRequired || "MCQ Options Count is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGenerate = async () => {
    if (alreadyExists) return;
    if (!validateGenerate()) return;
    if (regenerateCount >= MAX_REGENERATE) return;
    if (!assessment_id || !student_id) {
      setModalState({
        open: true,
        type: "Failed",
        message:
          t?.missingAssessmentInfo ||
          "Missing assessment or student information",
      });
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        school_id,
        assessment_id,
        student_id,
        current_count: regenerateCount,
        num_questions: Number(formData.num_questions),
        mcq_options: Number(formData.mcq_options),
        difficulty_level: formData.difficulty_level,
      };

      const response = await AxiosInstance.post(
        `/book/generateRemedialAssessment`,
        payload,
      );

      const respData = response?.data || {};
      if (respData.success || respData.status) {
        const rawQuestions =
          respData.questions ||
          respData.data?.questions ||
          respData.data?.study_material ||
          [];
        const newQuestions = Array.isArray(rawQuestions)
          ? rawQuestions.map(normalizeQuestion)
          : [];

        setGeneratedQuestions(newQuestions);
        setRegenerateCount((c) => c + 1);
        setHasGeneratedOnce(true);

        setModalState({
          open: true,
          type: "success",
          message:
            respData.message ||
            t?.assessmentGeneratedSuccess ||
            "Remedial generated successfully",
        });
      } else if (respData.remedial_id) {
        await fetchExistingRemedial(respData.remedial_id);
      } else {
        setModalState({
          open: true,
          type: "Failed",
          message:
            respData.message ||
            t?.failedToGenerateAssessment ||
            "Failed to generate assessment",
        });
      }
    } catch (error) {
      console.error("Error generating assessment:", error);
      setModalState({
        open: true,
        type: "Failed",
        message:
          error?.response?.data?.message ||
          t?.failedToGenerateAssessment ||
          "Failed to generate assessment",
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async () => {
    if (alreadyExists) return;
    if (generatedQuestions.length === 0) return;

    setSubmitting(true);
    try {
      const payload = {
        client_id: client_id,
        school_id,
        user_id,
        assessment_id,
        student_id,
        regeneration_count: regenerateCount,
        num_questions: Number(formData.num_questions),
        difficulty_level: formData.difficulty_level,
        mcq_options: Number(formData.mcq_options),
        duration: Number(formData.timeDuration),
        pass_percentage: "",
        questions: generatedQuestions.map((q, idx) => ({
          question_no: q.question_no ?? idx + 1,
          question: q.question,
          correct_option: q.correct_option,
          explanation: q.explanation,
          option_a: q.option_a,
          option_b: q.option_b,
          option_c: q.option_c,
          option_d: q.option_d,
          ...(q.option_e ? { option_e: q.option_e } : {}),
        })),
      };

      const response = await AxiosInstance.post(
        `/book/storeRemedialAssessment`,
        payload,
      );

      const respData = response?.data || {};
      if (respData.status || respData.success) {
        setModalState({
          open: true,
          type: "success",
          message:
            respData.message ||
            t?.assessmentSavedSuccess ||
            "Remedial saved successfully",
        });
        navigate(-1);
      } else {
        setModalState({
          open: true,
          type: "Failed",
          message:
            respData.message ||
            t?.failedToSaveAssessment ||
            "Failed to save assessment",
        });
      }
    } catch (error) {
      console.error("Error submitting assessment:", error);
      setModalState({
        open: true,
        type: "Failed",
        message:
          error?.response?.data?.message ||
          t?.failedToSaveAssessment ||
          "Failed to save assessment",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate(-1);

  const handleSuccessOk = () => {
    const { redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (redirectPath) {
      navigate(redirectPath);
    }
  };

  const regenerateLimitReached = regenerateCount >= MAX_REGENERATE;
  const generateDisabled =
    generating || regenerateLimitReached || alreadyExists;
  const submitDisabled =
    submitting || generatedQuestions.length === 0 || alreadyExists;

  // ==================== RENDER HELPERS ====================

  // Original two-panel card (used in fresh-create mode)
  const renderQuestionCard = (q, qIdx, mode) => {
    const optionCount = getOptionCountFromQuestion(q);
    const correctLetter = q.correct_option;
    const studentLetter = q.student_option;

    return (
      <div key={`${mode}-${qIdx}`} className={styles.questionBlock}>
        <div className={styles.questionText}>
          {q.question_no ?? qIdx + 1}. {q.question}
        </div>

        <div className={styles.optionsGrid}>
          {OPTION_LETTERS.slice(0, optionCount).map((letter) => {
            const optKey = `option_${letter}`;
            const optValue = q[optKey];
            if (optValue === undefined || optValue === null) return null;

            const isCorrect = correctLetter === letter;
            const isStudentPick = studentLetter === letter;
            const highlight = mode === "wrong" ? isStudentPick : isCorrect;

            return (
              <div key={letter} className={styles.optionCol}>
                <div
                  className={`${styles.optionCard} ${
                    highlight ? styles.optionCardCorrect : ""
                    }`}
                >
                  <span className={styles.radioWrapper}>
                    <input
                      type="radio"
                      name={`q-${mode}-${qIdx}`}
                      checked={highlight}
                      readOnly
                      className={styles.radioInput}
                    />
                    <span
                      className={`${styles.radioMark} ${
                        highlight ? styles.radioMarkChecked : ""
                        }`}
                    >
                      {highlight && <span className={styles.radioInner} />}
                    </span>
                  </span>
                  <span className={styles.optionText}>
                    {letter}) {optValue}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {mode === "wrong" ? (
          <div
            className={`${styles.explanationPanel} ${styles.explanationPanelWrong}`}
          >
            <FaTimesCircle
              className={`${styles.explanationIcon} ${styles.explanationIconWrong}`}
            />
            <div className={styles.explanationContent}>
              <div className={styles.explanationCorrectAnswer}>
                {correctLetter}) {q[`option_${correctLetter}`] || ""}
              </div>
              {q.explanation ? (
                <div className={styles.explanationText}>
                  {t?.explanation || "Explanation"} : {q.explanation}
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          q.explanation && (
            <div className={styles.explanationPanel}>
              <FaCheckCircle className={styles.explanationIcon} />
              <div className={styles.explanationContent}>
                <div className={styles.explanationCorrectAnswer}>
                  {correctLetter}) {q[`option_${correctLetter}`] || ""}
                </div>
                <div className={styles.explanationText}>
                  {t?.explanation || "Explanation"} : {q.explanation}
                </div>
              </div>
            </div>
          )
        )}
      </div>
    );
  };

  // View-style card (used in alreadyExists mode — matches RemedialView UI)
  const renderViewQuestionCard = (q, idx) => {
    const correctLetter = q.correct_option;

    return (
      <div key={`view-${idx}`} className={viewStyles.questionCard}>
        <p className={viewStyles.questionText}>
          {q.question_no ?? idx + 1}. {q.question}
        </p>

        <div className={viewStyles.optionsGrid}>
          {OPTION_LETTERS.map((letter) => {
            const optionText = q[`option_${letter}`];
            if (!optionText) return null;

            const isCorrectChoice = correctLetter === letter;
            let optionClass = viewStyles.option;
            if (isCorrectChoice) {
              optionClass += ` ${viewStyles.correctOption}`;
            }

            let radioClass = viewStyles.radio;
            if (isCorrectChoice) {
              radioClass += ` ${viewStyles.radioSelected} ${viewStyles.radioCorrect}`;
            }

            return (
              <div key={letter} className={optionClass}>
                <span className={radioClass}></span>
                <span className={viewStyles.optionLabel}>
                  {letter}) {optionText}
                </span>
              </div>
            );
          })}
        </div>

        <div
          className={`${viewStyles.explanationBanner} ${viewStyles.correctBanner}`}
        >
          <span className={viewStyles.bannerIcon}>✓</span>
          <div className={viewStyles.bannerContent}>
            <p className={viewStyles.bannerAnswer}>
              {correctLetter}) {q[`option_${correctLetter}`] || ""}
            </p>
            {q.explanation && (
              <p className={viewStyles.bannerExplanation}>
                <strong>
                  {translations?.labels?.explanation || "Explanation"} :{" "}
                </strong>
                {q.explanation}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================

  // ─── VIEW MODE: existing pending remedial — matches RemedialView UI ───
  if (alreadyExists) {
    return (
      <div className={viewStyles.container}>
        <PopupModal
          open={modalState.open}
          title={
            modalState.type === "success"
              ? translations?.labels?.success || t?.success || "Success"
              : translations?.labels?.failed || t?.failed || "Failed"
          }
          message={modalState.message}
          onClose={handleSuccessOk}
        />

        {/* Topic Information */}
        <div className={viewStyles.topicInfo}>
          <div className={viewStyles.topicLeft}>
            <p>
              <strong>{translations?.labels?.topic || "Topic"} :</strong>{" "}
              {assessmentMeta?.topic || "-"}
            </p>
            <p>
              <strong>{translations?.labels?.subTopic || "Sub Topic"} :</strong>{" "}
              {assessmentMeta?.sub_topic || "-"}
            </p>
          </div>
          <div className={viewStyles.topicRight}>
            <p>
              <strong>{translations?.labels?.subject || "Subject"} :</strong>{" "}
              {assessmentMeta?.subject || "-"}
            </p>
            <p>
              <strong>{translations?.labels?.class || "Class"} :</strong>{" "}
              {assessmentMeta?.class_name || assessmentMeta?.class || "-"}
              {assessmentMeta?.section_id
                ? ` - ${assessmentMeta.section_id}`
                : ""}
            </p>
          </div>
        </div>

        {/* Questions */}
        <div className={viewStyles.questionsList}>
          {generatedQuestions.length === 0 ? (
            <div className={styles.emptyState}>
              {t?.noQuestionsGenerated || "No questions to preview"}
            </div>
          ) : (
            generatedQuestions.map((q, idx) => renderViewQuestionCard(q, idx))
          )}
        </div>

        {/* Footer */}
        <div className={viewStyles.footer}>
          <button
            onClick={handleCancel}
            className={viewStyles.cancelButton}
            type="button"
          >
            {translations?.labels?.back || t?.back || "Back"}
          </button>
        </div>
      </div>
    );
  }

  // ─── CREATE MODE: regular two-panel layout ───
  return (
    <div>
      {/* CARD 1: Generate Assessment Form */}
      <div className={academicStyles.container}>
        <div className={academicStyles.header}>
          <div className={academicStyles.headerIcon}>
            <FaBook />
          </div>
          <h1 className={academicStyles.headerTitle}>
            {t?.generateremedialAssessment || "Generate Remedial Assessment"}
            {studentName ? ` — ${studentName}` : ""}
          </h1>
        </div>

        <div className="row">
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.numberOfQuestion || "Number of Question"}
              name="num_questions"
              value={formData.num_questions}
              onChange={handleInputChange}
              placeholder={t?.enterNumberOfQuestion || "Enter Number"}
              required
              error={errors.num_questions}
            />
          </div>
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={t?.difficultyLevel || "Difficulty Level"}
              name="difficulty_level"
              value={formData.difficulty_level}
              onChange={(value) =>
                handleDropdownChange("difficulty_level", value)
              }
              options={DIFFICULTY_OPTIONS}
              placeholder={t?.selectDifficultyLevel || "Select Difficulty"}
              required
              error={errors.difficulty_level}
            />
          </div>
          <div className="col-lg-4 px-2 col-md-6 col-sm-12">
            <CustomDropdown
              label={t?.mcqOptionsCount || "MCQ Options Count"}
              name="mcq_options"
              value={formData.mcq_options}
              onChange={(value) => handleDropdownChange("mcq_options", value)}
              options={MCQ_OPTION_OPTIONS}
              placeholder={t?.selectMcqOptions || "Select Options Count"}
              required
              error={errors.mcq_options}
            />
          </div>
          <div className="col-lg-3 px-2 col-md-6 col-sm-12">
            <CustomTextInput
              label={t?.timeDurationMins || "Time Duration (mins)"}
              name="timeDuration"
              type="text"              // text + inputMode lets us fully control input
              inputMode="numeric"
              maxLength={2}            // max value 30 is always 2 digits
              value={formData.timeDuration}
              onChange={(e) => {
                const raw = e.target.value;

                // Allow empty (user clearing the field)
                if (raw === "") {
                  handleInputChange(e);
                  return;
                }

                // Only digits, max 2 chars
                if (!/^\d{1,2}$/.test(raw)) return;

                const num = Number(raw);

                // 1-digit: 1–9 all valid (could still grow into 10–30)
                // Block "0" alone — leading zero
                if (raw.length === 1) {
                  if (num < 1) return;
                }

                // 2-digit: must be 10–30
                if (raw.length === 2) {
                  if (num < 10 || num > 30) return;
                }

                handleInputChange(e);
              }}
              onKeyDown={(e) => {
                // Block 'e', 'E', '+', '-', '.', ',' if they ever sneak through
                if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              required
              placeholder={t?.enterTimeDuration || "Enter time duration (1-30m)"}
              error={errors.timeDuration}
            />
          </div>
        </div>

        <div className={`${styles.buttonRowCenter} mt-2`}>
          <CustomButton
            type="button"
            variant="contained"
            className={academicStyles.addButton}
            fullWidth={false}
            onClick={handleGenerate}
            disabled={generateDisabled}
          >
            {generating
              ? t?.generating || "Generating..."
              : regenerateCount === 0
                ? t?.generate || "Generate"
                : `${t?.regenerate || "Re-Generate"} ${regenerateCount}/${MAX_REGENERATE}`}
          </CustomButton>
        </div>
      </div>

      {/* TWO-PANEL ROW */}
      <div className={`row mt-3 ${styles.previewRow}`}>
        <div className={`col-lg-6 col-md-12 px-2 mb-3 ${styles.panelCol}`}>
          <div className={styles.panelCard}>
            <div className={styles.panelHeader}>
              <div className={styles.panelHeaderIcon}>
                <FaClipboardList />
              </div>
              <h1 className={styles.panelHeaderTitle}>
                {t?.assessmentQuestions || "Assessment Questions"}
              </h1>
            </div>

            <div className={styles.previewBody}>
              {loadingAssessment ? (
                <div className={styles.emptyState}>
                  {t?.loading || "Loading..."}
                </div>
              ) : wrongQuestions.length === 0 ? (
                <div className={styles.emptyState}>
                  {t?.noWrongAnswers || "No wrong answers to review"}
                </div>
              ) : (
                wrongQuestions.map((q, idx) =>
                  renderQuestionCard(q, idx, "wrong"),
                )
              )}
            </div>
          </div>
        </div>

        <div className={`col-lg-6 col-md-12 px-2 mb-3 ${styles.panelCol}`}>
          <div className={styles.panelCard}>
            <div className={styles.panelHeader}>
              <div className={styles.panelHeaderIcon}>
                <FaClipboardList />
              </div>
              <h1 className={styles.panelHeaderTitle}>
                {t?.previewAssessment || "Preview Assessment"}
              </h1>
            </div>

            <div className={styles.previewBody}>
              {generating ? (
                <div className={styles.emptyState}>
                  {t?.generating || "Generating..."}
                </div>
              ) : generatedQuestions.length === 0 ? (
                <div className={styles.emptyState}>
                  {t?.noQuestionsGenerated ||
                    "Click Regenerate to create study material"}
                </div>
              ) : (
                generatedQuestions.map((q, idx) =>
                  renderQuestionCard(q, idx, "correct"),
                )
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={styles.buttonRowCenter}>
        <CustomButton
          type="button"
          variant="contained"
          className={academicStyles.cancelButton}
          fullWidth={false}
          onClick={handleCancel}
        >
          {t?.cancel || "Cancel"}
        </CustomButton>

        <CustomButton
          type="button"
          variant="contained"
          className={academicStyles.addButton}
          fullWidth={false}
          onClick={handleSubmit}
          disabled={submitDisabled}
        >
          {submitting
            ? t?.submitting || "Submitting..."
            : t?.submit || "Submit"}
        </CustomButton>
      </div>

      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? translations?.labels?.success || t?.success || "Success"
            : modalState.type === "Confirm"
              ? translations?.labels?.delete || t?.delete || "DELETE"
              : translations?.labels?.failed || t?.failed || "Failed"
        }
        message={modalState.message}
        onClose={handleSuccessOk}
      />
    </div>
  );
}

export default RemedialCreate;
